import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";

import {
  Button,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../Component";
import { unixDateConvertor } from "../../utilities/dateTimeUtils";
import {
  DATE_FORMATS,
  FACULTIES_ENUM,
  FACULTIES_ENUM_READABLE,
  SCHOLARSHIP_TYPES_ENUM,
  SCHOLARSHIP_TYPES_ENUM_READABLE,
  VACANCIES_ENUM,
} from "../../utilities/constants";
import UpdatableText from "./UpdatabaleText";
import RSelect from "../select/ReactSelect";
import BlockUpdateButton from "../button/BlockUpdateButton";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import { useStudentPageContext } from "../../hooks/useStudentPageContext";
import EventEmitter, { EVENT_TOPICS } from "../../utilities/eventUtils";
import { appendToFormData } from "../../utilities/formDataUtils";
import { studentService } from "../../services/studentService";

const DetailsPageHeader = ({
  title,
  name,
  createdDate,
  faculty,
  position,
  goBackPath,
  onGenerateClick,
  scholarship,
  buttonsToRender,
  isLoading,
  edit,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  // Hook must be called unconditionally (React rules)
  const studentContext = useStudentPageContext();
  const student = edit && id && studentContext ? studentContext.student : null;
  const setStudent =
    edit && id && studentContext ? studentContext.setStudent : null;

  const { handleSubmit, setValue } = useForm({
    defaultValues: {
      facultyType: student?.facultyType || faculty || "",
      feeType: student?.feeType || scholarship || "",
    },
  });
  const [onFetch] = useBeManualFetcher();
  const [hasEditClicked, setHasEditClicked] = useState(false);

  // Local state for select values
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [selectedFeeType, setSelectedFeeType] = useState(null);

  // Faculty options
  const facultyOptions = Object.keys(FACULTIES_ENUM).map((key) => ({
    value: FACULTIES_ENUM[key],
    label: FACULTIES_ENUM_READABLE[FACULTIES_ENUM[key]],
  }));

  // Fee Type options
  const feeTypeOptions = Object.keys(SCHOLARSHIP_TYPES_ENUM).map((key) => ({
    value: SCHOLARSHIP_TYPES_ENUM[key],
    label: SCHOLARSHIP_TYPES_ENUM_READABLE[SCHOLARSHIP_TYPES_ENUM[key]],
  }));

  const currentFacultyOption = facultyOptions.find(
    (option) => option.value === (student?.facultyType || faculty),
  );
  const currentFeeTypeOption = feeTypeOptions.find(
    (option) => option.value === (student?.feeType || scholarship),
  );

  // Initialize selected values
  useEffect(() => {
    if (edit && id) {
      const facultyVal = student?.facultyType || faculty || "";
      const feeTypeVal = student?.feeType || scholarship || "";

      const facultyOpt =
        facultyOptions.find((opt) => opt.value === facultyVal) ||
        currentFacultyOption ||
        null;
      const feeTypeOpt =
        feeTypeOptions.find((opt) => opt.value === feeTypeVal) ||
        currentFeeTypeOption ||
        null;

      setSelectedFaculty(facultyOpt);
      setSelectedFeeType(feeTypeOpt);

      setValue("facultyType", facultyVal);
      setValue("feeType", feeTypeVal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student, faculty, scholarship, edit, id, setValue]);

  function handleOnEditClicked() {
    setHasEditClicked((prev) => !prev);
  }

  async function onFormSubmit(form) {
    if (!id || !edit) return;

    const facultyType = selectedFaculty?.value || form.facultyType || "";
    const feeType = selectedFeeType?.value || form.feeType || "";

    console.log("Submitting with values:", { facultyType, feeType });

    // Use FormData format (like other student updates)
    const newFormData = new FormData();
    appendToFormData(newFormData, {
      facultyType: facultyType,
      feeType: feeType,
    });

    return await onFetch({
      action: () =>
        studentService.updateStudentFacultyAndFeeType(id, newFormData),
      onLoad: (response) => {
        EventEmitter.emit(EVENT_TOPICS.ON_STUDENT_UPDATED, { response });
        if (setStudent) {
          setStudent(response);
        }
        handleOnEditClicked();
      },
      successMessage: "Faculty and Fee Type updated successfully",
    });
  }

  return (
    <BlockHead size="sm">
      <Button
        color="white bg-white"
        outline
        className="mb-3"
        onClick={(e) => {
          e.preventDefault();
          navigate(goBackPath);
        }}
        iconName="arrow-left"
        title="Back"
      />
      {edit && id ? (
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page noText>
                {t(title)} <strong className="text-gray">{name}</strong>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  {createdDate && (
                    <li className="mr-5">
                      {t("Created On")}:{" "}
                      <h6>
                        {unixDateConvertor(createdDate, DATE_FORMATS.DATE_TIME)}
                      </h6>
                    </li>
                  )}
                  {faculty && (
                    <li className="mr-5">
                      <UpdatableText
                        title="Faculty"
                        defaultValue={
                          FACULTIES_ENUM_READABLE[
                            student?.facultyType || faculty
                          ]
                        }
                        editableValueComponent={
                          <RSelect
                            options={facultyOptions}
                            value={selectedFaculty}
                            onChange={(selectedOption) => {
                              setSelectedFaculty(selectedOption);
                              setValue(
                                "facultyType",
                                selectedOption?.value || "",
                              );
                            }}
                            placeholder="Select Faculty"
                            styles={{
                              control: (base) => ({
                                ...base,
                                minWidth: "300px",
                                width: "100%",
                              }),
                              container: (base) => ({
                                ...base,
                                minWidth: "300px",
                                width: "100%",
                              }),
                            }}
                          />
                        }
                        onEditClicked={hasEditClicked}
                      />
                    </li>
                  )}
                  {scholarship && (
                    <li>
                      <UpdatableText
                        title="Fee Type"
                        defaultValue={
                          SCHOLARSHIP_TYPES_ENUM_READABLE[
                            student?.feeType || scholarship
                          ]
                        }
                        editableValueComponent={
                          <RSelect
                            options={feeTypeOptions}
                            value={selectedFeeType}
                            onChange={(selectedOption) => {
                              setSelectedFeeType(selectedOption);
                              setValue("feeType", selectedOption?.value || "");
                            }}
                            placeholder="Select Fee Type"
                            styles={{
                              control: (base) => ({
                                ...base,
                                minWidth: "300px",
                                width: "100%",
                              }),
                              container: (base) => ({
                                ...base,
                                minWidth: "300px",
                                width: "100%",
                              }),
                            }}
                          />
                        }
                        onEditClicked={hasEditClicked}
                      />
                    </li>
                  )}
                  {position && (
                    <li>
                      {t("Applied for")}: <h6>{VACANCIES_ENUM[position]}</h6>
                    </li>
                  )}
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent className="d-flex align-items-center">
              {buttonsToRender?.length > 0 ? (
                <>
                  {buttonsToRender?.map((button, index) => {
                    return (
                      <React.Fragment key={index}>
                        {!button.hideCondition ? (
                          <Button
                            color={button.color}
                            className={button.className}
                            onClick={(e) => {
                              e.preventDefault();
                              button.onClick();
                            }}
                            iconName={button.icon}
                            title={button.title}
                            disabled={isLoading === button.title}
                            isLoading={isLoading === button.title}
                          />
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </>
              ) : null}
              {edit && (
                <div className="ml-auto">
                  <BlockUpdateButton
                    hasEditClicked={hasEditClicked}
                    handleOnEditClicked={handleOnEditClicked}
                    className="m-0 ml-2"
                  />
                </div>
              )}
              {title === "Employee -" && (
                <Button
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    onGenerateClick();
                  }}
                  iconName="file-download"
                  title="Generate CV"
                />
              )}
            </BlockHeadContent>
          </BlockBetween>
        </form>
      ) : (
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h3" page noText>
              {t(title)} <strong className="text-gray">{name}</strong>
            </BlockTitle>
            <BlockDes className="text-soft">
              <ul className="list-inline">
                {createdDate && (
                  <li className="mr-5">
                    {t("Created On")}:{" "}
                    <h6>
                      {unixDateConvertor(createdDate, DATE_FORMATS.DATE_TIME)}
                    </h6>
                  </li>
                )}
                {faculty && (
                  <li className="mr-5">
                    {t("Faculty")}: <h6>{FACULTIES_ENUM_READABLE[faculty]}</h6>
                  </li>
                )}
                {scholarship && (
                  <li>
                    {t("Fee Type")}:{" "}
                    <h6>{SCHOLARSHIP_TYPES_ENUM_READABLE[scholarship]}</h6>
                  </li>
                )}
                {position && (
                  <li>
                    {t("Applied for")}: <h6>{VACANCIES_ENUM[position]}</h6>
                  </li>
                )}
              </ul>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent>
            {title === "Employee -" && (
              <Button
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  onGenerateClick();
                }}
                iconName="file-download"
                title="Generate CV"
              />
            )}
          </BlockHeadContent>
          {buttonsToRender?.length > 0 ? (
            <BlockHeadContent>
              {buttonsToRender?.map((button, index) => {
                return (
                  <React.Fragment key={index}>
                    {!button.hideCondition ? (
                      <Button
                        color={button.color}
                        className={button.className}
                        onClick={(e) => {
                          e.preventDefault();
                          button.onClick();
                        }}
                        iconName={button.icon}
                        title={button.title}
                        disabled={isLoading === button.title}
                        isLoading={isLoading === button.title}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
            </BlockHeadContent>
          ) : null}
        </BlockBetween>
      )}
    </BlockHead>
  );
};

export default DetailsPageHeader;
